import { Controller } from "@hotwired/stimulus"

// Inspired by: https://htmldom.dev/create-resizable-split-views/

// How far an arrow key moves the divider, as a share of the container's width.
const NUDGE = 0.02
// A press that moves no further than this is a click, not a drag: a hand is never perfectly still.
const DRAG_THRESHOLD_PX = 3

export default class extends Controller {
  static targets = ["left", "divider", "right", "rightArrow", "leftArrow"]
  // All optional. A split that sets none of them is a plain draggable divider, sized in pixels.
  // One that sets any is "managed": the left pane's width is kept as a share of the container (a
  // percentage, so it survives a window resize) and the divider reports it to assistive tech.
  static values = {
    // The share (0 to 1) the left pane starts with and reset() returns to. The page's markup
    // cannot say this reliably: a page restored from Turbo's cache carries whatever inline width
    // it was left with.
    default: Number,
    // The smallest and largest share the left pane may take.
    min: Number,
    max: Number,
    // A localStorage key. The left pane's share is remembered there and restored the next time a
    // page with the same key opens, so the split a person chose follows them.
    storageKey: String
  }

  initialize() {
    this.boundMouseDown = this.mouseDown.bind(this);
    this.boundMouseMove = this.mouseMove.bind(this);
    this.boundMouseUp = this.mouseUp.bind(this);
  }

  connect() {
    this.dividerTarget.addEventListener("mousedown", this.boundMouseDown);
    this.maximized = false;
    // A page cached by Turbo in the middle of a drag comes back with its panes dead to the mouse.
    this.endDrag();

    const share = this.rememberedShare() ?? (this.hasDefaultValue ? this.defaultValue : null);
    if (share !== null) this.applyShare(share);
  }

  disconnect() {
    // A drag in flight when the page is replaced must not go on listening for the mouse, nor
    // leave the panes dead to it (Turbo caches the page as it stands).
    this.endDrag();
    this.dividerTarget.removeEventListener("mousedown", this.boundMouseDown);
  }

  get managed() {
    return this.hasDefaultValue || this.hasMinValue || this.hasMaxValue || this.hasStorageKeyValue;
  }

  mouseDown(e) {
    // Only a plain primary-button press drags. A right click, or a Ctrl-click on a Mac (which
    // still reports the primary button), opens a context menu that swallows the mouseup, which
    // would leave the divider following the mouse.
    if (e.button !== 0 || e.ctrlKey) return;

    // Get the current mouse position
    this.x = e.clientX;
    this.y = e.clientY;
    this.leftWidth = this.leftTarget.getBoundingClientRect().width;
    this.rightWidth = this.rightTarget.getBoundingClientRect().width;
    // Measured once: it does not change during a drag, and measuring per mousemove forces a layout.
    this.dragContainerWidth = this.managed ? this.containerWidth() : 0;
    this.dragged = false;

    document.body.classList.add("cursor-col-resize");

    this.leftTarget.style.userSelect = 'none';
    this.leftTarget.style.pointerEvents = 'none';

    this.rightTarget.style.userSelect = 'none';
    this.rightTarget.style.pointerEvents = 'none';

    // Attach the listeners to `document`
    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('mouseup', this.boundMouseUp);
  }

  mouseMove(e) {
    // The button was released somewhere that never told us (outside the window, over a menu).
    if (e.buttons === 0) {
      this.mouseUp(e);
      return;
    }

    // How far the mouse has been moved
    const dx = e.clientX - this.x;
    const dy = e.clientY - this.y;
    if (!this.dragged && Math.abs(dx) <= DRAG_THRESHOLD_PX) return;
    this.dragged = true;

    let newLeftWidth = (this.leftWidth + dx);
    if (this.dragContainerWidth > 0) {
      this.dragShare = this.clamp(newLeftWidth / this.dragContainerWidth);
      newLeftWidth = this.dragShare * this.dragContainerWidth;
      this.reportShare(this.dragShare);
    }
    this.leftTarget.style.width = `${newLeftWidth}px`;

    // No need to adjust the right side.  It's set to grow to fill the remaining space.
  };

  mouseUp(e) {
    const dragged = this.dragged;
    this.endDrag();

    // A click on the divider with no movement (to focus it) changes nothing.
    if (dragged && this.dragContainerWidth > 0) this.setShare(this.dragShare);
    // A drag sized in pixels leaves no share to nudge from; the next nudge measures.
    else if (dragged) this.share = null;
    // The last two presses: a double click is a reset only when neither was a drag.
    this.pressesDragged = [this.pressesDragged?.[1] ?? false, dragged];
  }

  // Stop following the mouse and give the panes back to it.
  endDrag() {
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);

    this.leftTarget.style.removeProperty('user-select');
    this.leftTarget.style.removeProperty('pointer-events');

    this.rightTarget.style.removeProperty('user-select');
    this.rightTarget.style.removeProperty('pointer-events');

    document.body.classList.remove("cursor-col-resize");
    this.dragged = false;
  }

  toggle() {
    this.share = null;
    if (this.maximized) {
      this.leftTarget.style.width = `${this.initialLeftWidth}px`;
      this.leftArrowTarget.classList.add("hidden");
      this.rightArrowTarget.classList.remove("hidden");
      this.maximized = false;
    } else {
      this.initialLeftWidth = this.leftTarget.getBoundingClientRect().width;
      const newLeftWidth = 0.9 * this.element.getBoundingClientRect().width;
      this.leftTarget.style.width = `${newLeftWidth}px`;
      this.leftArrowTarget.classList.remove("hidden");
      this.rightArrowTarget.classList.add("hidden");
      this.maximized = true;
    }
  }

  // Arrow keys on the focused divider: `keydown->split-view#nudge`. With a modifier they are the
  // browser's (Alt+Left goes back), so those are left alone.
  nudge(e) {
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    const step = { ArrowLeft: -NUDGE, ArrowRight: NUDGE }[e.key];
    const share = this.share ?? this.currentShare();
    if (!step || share === null) return;

    e.preventDefault();
    this.setShare(share + step);
  }

  // Back to the default split, forgetting the remembered one: `dblclick->split-view#reset`.
  // Two small drags in quick succession also end in a dblclick; that is fine-tuning, not a reset.
  reset() {
    if (this.pressesDragged?.some(Boolean)) return;

    if (this.hasStorageKeyValue) this.storage("removeItem");
    if (this.hasDefaultValue) {
      this.applyShare(this.defaultValue);
    } else {
      this.leftTarget.style.removeProperty("width");
      this.share = null;
      for (const name of ["aria-valuenow", "aria-valuemin", "aria-valuemax"]) this.dividerTarget.removeAttribute(name);
    }
  }

  // Sizes the left pane to a share of the container and remembers it.
  setShare(share) {
    const applied = this.applyShare(share);
    if (this.hasStorageKeyValue) this.storage("setItem", String(applied));
  }

  applyShare(share) {
    // Four decimals: a hundredth of a percent, and no floating-point tails in what is stored.
    const clamped = Math.round(this.clamp(share) * 10000) / 10000;
    this.share = clamped;
    this.leftTarget.style.width = this.percent(clamped);
    this.reportShare(clamped);
    return clamped;
  }

  // A focusable separator has to say where it is (WAI-ARIA window splitter).
  reportShare(share) {
    this.dividerTarget.setAttribute("aria-valuenow", Math.round(share * 100));
    this.dividerTarget.setAttribute("aria-valuemin", Math.round((this.hasMinValue ? this.minValue : 0) * 100));
    this.dividerTarget.setAttribute("aria-valuemax", Math.round((this.hasMaxValue ? this.maxValue : 1) * 100));
  }

  // The left pane's share of the container's content box, which is what a percentage width
  // resolves against: the padding is left out (measuring against the border box would shrink the
  // pane a little on every drag), and the divider is not, so it comes out of the right pane's side.
  currentShare() {
    const containerWidth = this.containerWidth();
    if (!(containerWidth > 0)) return null;
    return this.leftTarget.getBoundingClientRect().width / containerWidth;
  }

  containerWidth() {
    const style = window.getComputedStyle(this.element);
    const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
    return this.element.clientWidth - padding;
  }

  rememberedShare() {
    if (!this.hasStorageKeyValue) return null;
    const share = parseFloat(this.storage("getItem"));
    return Number.isFinite(share) && share > 0 && share < 1 ? share : null;
  }

  clamp(share) {
    if (this.hasMinValue) share = Math.max(share, this.minValue);
    if (this.hasMaxValue) share = Math.min(share, this.maxValue);
    return share;
  }

  percent(share) {
    return `${Math.round(share * 10000) / 100}%`;
  }

  // localStorage can be unavailable (private browsing, blocked storage); the divider still works.
  storage(method, value) {
    try {
      return window.localStorage[method](this.storageKeyValue, value);
    } catch {
      return null;
    }
  }
}
